import { defineStore } from 'pinia'
import axios from 'axios'
import router from '@/router'
import { useCookies } from "vue3-cookies"
import { getResponse, getError } from '@/composables/api'
import { useToastsStore } from '@/stores/notifications/toasts'
import { useResetStore } from '@/stores/reset'
import { useAppStore } from '@/stores/app/app'
import { useViewLoadingStore } from '@/stores/loading/view-loading'

export const useAuthStore = defineStore({
	id: 'auth',
	state: () => ({
		isAuthenticated: false,
		checkedAuthentication: false,
		loginError: null,
		emailChecked: false,
		emailIsAvailable: false,
		registered: false,

		// Errors
		loginError: null,

		// Login Verification
		verificationResent: false,
		loginVerified: false,
		gotCodeResponse: false,
		loginVerificationError: '',
		verifyLoginCodeResent: false,

		// Password Reset
		gotPasswordResponse: false,
		passwordIsReset: false,
		passwordResetError: '',

		externalEndpoint: process.env.APP_URL,
      storeToasts: useToastsStore(),
      storeReset: useResetStore(),
      storeApp: useAppStore(),
      storeLoading: useViewLoadingStore(),
	}),
	actions: {
		/**
			* Function: getCSRF
			* @description Sends an intial request to the API
			* to get the CSRF token, the token is automatically
			* sent with subsequent requests
		*/
		getCSRF() {
			return axios.get(`${process.env.APP_URL}/sanctum/csrf-cookie`)
	  	},
	  /**
		* Function: login
		* @description Sends user name and password to the
		* API to authenticate the user, once authenticated
		* sets isAuthenticated to true
		*
		* @param {Object} payload username and password
	  */
	  async login(payload) {
			try {
				await this.getCSRF()
				const response = await axios.post(`${this.externalEndpoint}/login`, payload)
				if(response) {
					this.isAuthenticated = true
					this.loginError = null
				}
			} catch (error) {
				this.unAuthenticate()
				this.loginError = getError(error)
			}
	  },
	  register(formData) {
			this.registered = false
		  axios.post(`${this.externalEndpoint}/register`, formData).then((response) => {
				if(response.data.success)
					this.registered = true
		  }).catch((response) => {
				this.registered = false
			  console.log(getError(response))
		  })
	  },
	  /**
		  * Verify login code
		  * Checks to see if the code is valid when logging in for the first time
	  */
		verifyLogin(params) {
			this.gotCodeResponse = false
			this.loginVerified = false

		  axios.post('verify-email', { id: params.id, code: params.code })
		  .then(response => {
				this.gotCodeResponse = true

				if (response.data.success) {
					this.loginVerified = true
					this.loginVerificationError = ''
				}
			})
			.catch((error) => {
				if(getError(error) == 'user_not_found') {
					this.gotCodeResponse = true
					this.loginVerified = false
					this.loginVerificationError = getError(error)
				}
			})
  		},
		  sendPassword(formData) {
				this.passwordSent = false
			  axios.post(`send-password`, formData).then((response) => {
					if(response.data.success)
						this.passwordSent = true
			  }).catch((response) => {
					this.passwordSent = false
			  })
		  },
	  /**
		  * passwordReset
	  */
		passwordReset(form) {
			this.gotPasswordResponse = false
			this.passwordIsReset = false

		  axios.post('reset-password', form)
		  .then(response => {
				this.gotPasswordResponse = true

				if (response.data.success) {
					this.passwordIsReset = true
					this.passwordResetError = ''
				}
			})
			.catch((error) => {
				this.storeToasts.sendToast(error, 'danger')
			})
  		},
	  /**
		  * checkEmail
		  * Sends a request to check the email address
		  * that the user has entered while registering.
		  *
		  * @param String email
		  * @return void
		  *
	  */
	  checkEmail( email ) {
		  this.emailChecked = false

		  axios.post('check-email', { email: email })
		  .then(response => {
			  if(response.data.success) {
				  this.emailChecked = true
				  this.emailIsAvailable = true
			  } else {
				  this.emailChecked = true
				  this.emailIsAvailable = false
			  }
		  }).catch(response => {
			this.emailChecked = true
			this.emailIsAvailable = false
		  })
	  },
	  logout() {
		axios.post(`${this.externalEndpoint}/logout`).then(response => {
			this.unAuthenticate()
		})
	  },
	  resendVerification(email) {
		axios.post('resend-verification', { email: email }).then(response => {
			this.verificationResent = true
		})
	  },
	  /**
		* Function: checkAuthentication
		* @description Sends an authentication request to
		* the server to check if the user is authenticated
	  */
	 checkAuthentication() {
  		this.checkedAuthentication = false // Reset the flag before checking

		// Return a new promise
		return new Promise((resolve, reject) => {
			axios.get('auth')
				.then(response => {
				if (response.data?.authenticated) {
					if (response?.data?.mode) {
						this.storeApp.updateMode(response?.data?.mode, response?.data?.version, response?.data?.stage)
					}
					this.isAuthenticated = true
					this.checkedAuthentication = true
					resolve(response)
				} else {
					window.location.href = '/maintenance.html'
				}
				})
				.catch(error => {
					if(error?.response?.status == 503) {
						this.storeApp.updateMode('maintenance')
					} else {
						this.checkedAuthentication = true
						reject(error)
					}
				})
			})
		},
	  /**
		* Function: unAuthenticate
		* @description Clear all stored authentication data
		* and redirect to login page
	  */
		/**
		 * Function: unAuthenticate
		 * @description Clear all stored authentication data and redirect to login page with a full page reload
		 */
		unAuthenticate() {
			this.storeLoading.updateLoading(true)
			const { cookies } = useCookies()
			cookies.remove('codeginnel_session')
			cookies.remove('XSRF-TOKEN')
			// Redirect to login with logout query and ensure a full page reload
			window.location.href = '/';
		}
	}
})